import { ChatPanel } from "./ChatPanel";
import { Disclaimer } from "./Disclaimer";
import { ExteriorViewLink } from "./ExteriorViewCard";
import { LockPanel } from "./LockPanel";
import { PropertyMap } from "./PropertyMap";
import { PropertySummaryCard } from "./PropertySummaryCard";

/**
 * FreePropertyView — Task 9.0/9.1 chat-forward free tier.
 *
 * Replaces the old monolithic streaming report. Layout:
 *   - At-A-Glance header: normalized address + Amazon Location map
 *     (Task 9.2). Renders instantly, no LLM. The map falls back to a
 *     placeholder if coords couldn't be resolved.
 *   - ChatPanel (forward variant): the PRIMARY surface — "ask anything
 *     about this property", grounded in web data, free-tier turn-limited.
 *   - Lock_Panels: paid features teased alongside, not blocking the chat.
 *
 * The chat targets the property by place_id (no score — free tier has none).
 */

interface Props {
  address: string;
  placeId: string;
  askingPrice: number | null;
  lat: number | null;
  lon: number | null;
  onUpgradeClick: () => void;
  // usage-tracking-limits Req 11 — spend the one-time free Pro trial on THIS
  // property. Absent/omitted once the trial is spent (trialSpent), when the
  // upgrade CTA takes over instead.
  onTryPro?: () => void;
  tryProPending?: boolean;
  trialSpent?: boolean;
  tryProError?: string | null;
  // Why the trial is unavailable when THIS account never spent one (e.g.
  // "email_already_used" — the email's grant belongs to another account).
  trialSpentReason?: string | null;
  // Permanent unlock (Req 3.5/11.4): view the report the trial bought.
  onViewUnlocked?: () => void;
  unlockedAddress?: string | null;
}

export function FreePropertyView({
  address,
  placeId,
  askingPrice,
  lat,
  lon,
  onUpgradeClick,
  onTryPro,
  tryProPending = false,
  trialSpent = false,
  tryProError = null,
  trialSpentReason = null,
  onViewUnlocked,
  unlockedAddress = null,
}: Props) {
  return (
    <article className="free-property-view">
      <section className="at-a-glance" aria-label="Property at a glance">
        <div className="address-row">
          <h2 className="property-address">{address}</h2>
          {/* Curb photo (curb-photo): inline Street View trigger by the address,
              opens a modal (map covered while open) — never embedded beside the
              Amazon Location map, per Google's co-rendering restriction. */}
          <ExteriorViewLink placeId={placeId} />
        </div>
        {lat != null && lon != null ? (
          <PropertyMap lat={lat} lon={lon} address={address} />
        ) : (
          <div className="property-map-placeholder" role="img" aria-label="Map unavailable">
            <span>Map unavailable</span>
          </div>
        )}
      </section>

      <PropertySummaryCard placeId={placeId} address={address} onUpgradeClick={onUpgradeClick} />

      <div className="free-layout">
        <div className="free-chat-col">
          <ChatPanel
            placeId={placeId}
            variant="forward"
            onUpgradeClick={onUpgradeClick}
          />
        </div>

        <aside className="free-locks-col" aria-label="More with PriceTilt Pro">
          {/* usage-tracking-limits Req 11.1 — explicit "Try PriceTilt Pro"
              action. Spends the one-time free verified report on THIS property.
              Never auto-consumed (Req 11.2). Once the trial is spent, the CTA
              swaps to upgrade messaging (Req 11.5) instead of silently hiding. */}
          {onTryPro && !trialSpent && (
            <div className="try-pro-card">
              <h3>Try PriceTilt Pro — free</h3>
              <p>
                Unlock the full verified Tilt Map, licensed property data, and
                the AI analyst brief for <strong>{address}</strong>. This is your
                one free Pro report.
              </p>
              <button
                type="button"
                className="try-pro-button"
                onClick={onTryPro}
                disabled={tryProPending || askingPrice == null}
              >
                {tryProPending ? "Generating your Pro report…" : "Try PriceTilt Pro"}
              </button>
              {askingPrice == null && (
                <p className="try-pro-hint" role="note">
                  Add an asking price above to see your offer range.
                </p>
              )}
              {tryProError && (
                <p className="try-pro-error" role="alert">
                  {tryProError}
                </p>
              )}
            </div>
          )}
          {onTryPro && trialSpent && (
            <div className="try-pro-card try-pro-spent">
              <h3>
                {trialSpentReason === "email_already_used"
                  ? "Free Pro report unavailable"
                  : "You’ve used your free Pro report"}
              </h3>
              <p>
                {trialSpentReason === "email_already_used" ? (
                  <>
                    This email address already used its one free Pro report
                    (possibly on a previous account). Subscribe to PriceTilt Pro
                    for verified reports on any address.
                  </>
                ) : onViewUnlocked && unlockedAddress ? (
                  <>
                    Your free Pro report unlocked{" "}
                    <strong>{unlockedAddress}</strong> — it&rsquo;s yours to
                    revisit anytime. Subscribe to PriceTilt Pro for verified
                    reports on any address.
                  </>
                ) : (
                  <>
                    Your one free Pro report has been spent. Subscribe to
                    PriceTilt Pro to unlock the verified Tilt Map, licensed
                    property data, and the AI analyst brief on any address.
                  </>
                )}
              </p>
              {onViewUnlocked && (
                <button
                  type="button"
                  className="try-pro-button"
                  onClick={onViewUnlocked}
                >
                  View your unlocked Pro report
                </button>
              )}
              <button
                type="button"
                className={
                  onViewUnlocked
                    ? "try-pro-button try-pro-secondary"
                    : "try-pro-button"
                }
                onClick={onUpgradeClick}
              >
                Upgrade to PriceTilt Pro
              </button>
            </div>
          )}
          <h3>More with PriceTilt Pro</h3>
          {/* Six locked features, mirrored verbatim from the marketing repo's
              LOCKED_FEATURES (pricetilt-marketing src/config/pricing.ts) —
              keep the two lists in sync. Each benefit line answers something
              the free view surfaced; owner equity is the headline lock. */}
          <LockPanel
            feature="tilt_map"
            icon="🗺️"
            title="Verified Tilt Map"
            description={
              "Your exact negotiation position + AI-generated offer range. " +
              "The starting number, not a guess."
            }
            onUpgradeClick={onUpgradeClick}
          />
          <LockPanel
            feature="owner_equity"
            icon="🔒"
            title="Owner & equity profile"
            description={
              "Balance owed, equity, tenure, motivation signals. Who " +
              "you're really negotiating with."
            }
            onUpgradeClick={onUpgradeClick}
          />
          <LockPanel
            feature="fair_value_comps"
            icon="📊"
            title="Verified fair value & comps"
            description={
              "Licensed valuation + comparable sales. The number behind " +
              "the asking price."
            }
            onUpgradeClick={onUpgradeClick}
          />
          <LockPanel
            feature="permits"
            icon="🔨"
            title="Permit & renovation history"
            description={
              "What was actually permitted, not just what the listing claims."
            }
            onUpgradeClick={onUpgradeClick}
          />
          <LockPanel
            feature="flood_risk"
            icon="🌊"
            title="Flood & insurance risk"
            description={
              "FEMA zone + cost impact. The carrying cost the listing " +
              "won't quantify."
            }
            onUpgradeClick={onUpgradeClick}
          />
          <LockPanel
            feature="saved_reports"
            icon="📌"
            title="Saved properties, comparison & alerts"
            description={
              "Track leverage changes across your hunt. Know when a " +
              "property tips in your favor."
            }
            onUpgradeClick={onUpgradeClick}
          />
        </aside>
      </div>

      <Disclaimer />
    </article>
  );
}
